import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { useApiCall } from '../hooks/useApiCall';
import '../styles/Page.css';

// Sanitize HTML from the rich text editor:
// - Strips inline styles (prevents custom colour / font-size theming)
// - Strips all event handlers (security)
// - Keeps structural tags: headings, paragraphs, lists, tables, blockquotes,
//   figures, images (with src/alt/width/height), links, hr, strong, em, code
const PURIFY_CONFIG = {
  ALLOWED_TAGS: [
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'p', 'br', 'hr',
    'ul', 'ol', 'li',
    'strong', 'b', 'em', 'i', 'u', 's',
    'a',
    'img', 'figure', 'figcaption',
    'blockquote', 'footer',
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'colgroup', 'col',
    'code', 'pre',
    'div', 'span',
  ],
  ALLOWED_ATTR: [
    // links
    'href', 'target', 'rel', 'download',
    // images
    'src', 'alt', 'width', 'height', 'loading',
    // style — restricted to img only via hook in sanitizeContent()
    'style',
    // tables
    'colspan', 'rowspan',
    // pdf block marker
    'data-type',
    // preset block marker
    'data-block',
  ],
  FORBID_ATTR: ['class', 'id'],
  FORCE_BODY: true,
};

// Only keep safe layout CSS on <img> and text-alignment on block elements;
// strip all other styles for security.
const BLOCK_TAGS = new Set(['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'DIV', 'LI', 'TD', 'TH', 'BLOCKQUOTE', 'FOOTER']);

function restrictStyles(node) {
  if (!node.hasAttribute || !node.hasAttribute('style')) return;
  if (node.tagName === 'IMG') {
    const safe = (node.getAttribute('style') || '')
      .split(';')
      .map(s => s.trim())
      .filter(s => /^(width|height|float|display|margin(-left|-right|-top|-bottom)?)\s*:/i.test(s))
      .join('; ');
    safe ? node.setAttribute('style', safe) : node.removeAttribute('style');
  } else if (BLOCK_TAGS.has(node.tagName)) {
    // Allow text-align only — everything else is stripped
    const safe = (node.getAttribute('style') || '')
      .split(';')
      .map(s => s.trim())
      .filter(s => /^text-align\s*:/i.test(s))
      .join('; ');
    safe ? node.setAttribute('style', safe) : node.removeAttribute('style');
  } else {
    node.removeAttribute('style');
  }
}

function sanitizeContent(html) {
  DOMPurify.addHook('afterSanitizeAttributes', restrictStyles);
  const result = DOMPurify.sanitize(html, PURIFY_CONFIG);
  DOMPurify.removeHook('afterSanitizeAttributes');
  return result;
}

export default function Page() {
  const { slug } = useParams();
  const apiCall = useApiCall();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPage = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await apiCall('pages/get-by-slug', 'POST', { slug });
        if (response && response.success && response.data) {
          setPage(response.data.page);
          
          // Update page title
          if (response.data.page.title) {
            document.title = `${response.data.page.title} - Building Depot Suriname`;
          }
          
          // Update meta description
          if (response.data.page.meta_description) {
            const metaDescription = document.querySelector('meta[name="description"]');
            if (metaDescription) {
              metaDescription.setAttribute('content', response.data.page.meta_description);
            }
          }
        } else {
          setError('Page not found');
        }
      } catch (err) {
        setError('Failed to load page');
        console.error('Error fetching page:', err);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchPage();
    }
  }, [slug]);

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="page-container">
          <div className="page-loading">
            <div className="loading-spinner"></div>
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="page-wrapper">
        <div className="page-container">
          <div className="page-error">
            <h1>Page Not Found</h1>
            <p>The page you're looking for doesn't exist or has been removed.</p>
            <button onClick={() => window.location.href = '/'} className="btn-primary">Return Home</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="page-container">
        {/* Page Header */}
        <div className="page-header">
          <h1>{page.title}</h1>
        </div>

        {/* Page Content */}
        <div className="page-content-wrapper">
          <div
            className="page-content" 
            dangerouslySetInnerHTML={{ __html: sanitizeContent(page.content) }}
          />
          
          {page.updated_at && (
            <div className="page-footer">
              <small>Last updated: {new Date(page.updated_at).toLocaleDateString()}</small>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
